// TODO: CRDB names this attribution_model; rename it attribution_distribution there.
import { AttributionEffectEventType as AttributionOutcomeType } from "@redotech/crdb-enums/attribution/attribution-effect-event-type";
import { AttributionModel as AttributionDistribution } from "@redotech/crdb-enums/attribution/attribution-model";
import { durationToSeconds, TimeUnit } from "@redotech/redo-common-model/time";
import * as z from "zod";

/** Subset of TimeUnit values the attribution UI exposes. */
export const ATTRIBUTION_TIME_UNITS = [
  TimeUnit.MINUTE,
  TimeUnit.HOUR,
  TimeUnit.DAY,
] as const;

/**
 * The longest attribution window, everywhere. Persistence rejects anything
 * larger and the analytics filters stop here. Raise it when merchants ask.
 */
export const MAX_ATTRIBUTION_WINDOW_DAYS = 30;

/** Time window looked backward from an outcome. */
export const durationSchema = z
  .object({
    value: z.number().int().positive(),
    unit: z.enum(ATTRIBUTION_TIME_UNITS),
  })
  .refine(
    (d) =>
      durationToSeconds(d.value, d.unit) <=
      MAX_ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60,
    { message: `window exceeds ${MAX_ATTRIBUTION_WINDOW_DAYS} days` },
  );
export type Duration = z.infer<typeof durationSchema>;

/**
 * Something suggesting a touchpoint caused an outcome.
 * TODO: CRDB still names this `attribution_cause_event_type`, and outcomes
 * `attribution_effect_event_type` (imported here as AttributionOutcomeType);
 * rename both there too.
 */
export enum AttributionEvidenceType {
  // Email
  EMAIL_DELIVERED = "email_delivered",
  EMAIL_OPEN = "email_open",
  EMAIL_CLICK = "email_click",
  // SMS — SMS_DELIVERED and SMS_OPEN intentionally omitted: carriers don't
  // reliably report either, so they'd be misleading qualifying signals.
  SMS_SEND = "sms_send",
  SMS_CLICK = "sms_click",
  /** Discount codes and gift card ids are strong evidence that we influenced the order.
   *  They're both grouped under CODE_REDEEMED because there is currently no known
   *  reason to treat them differently. */
  CODE_REDEEMED = "code_redeemed",
}

/** A redeemed code is near proof, so its window is the longest, and fixed. */
export const CODE_REDEEMED_WINDOW: Duration = {
  value: MAX_ATTRIBUTION_WINDOW_DAYS,
  unit: TimeUnit.DAY,
};

/** How far before an order the judge reads conversations. Fixed and hidden: merchants see no window. */
export const MEANINGFUL_CONVERSATION_WINDOW: Duration = {
  value: 14,
  unit: TimeUnit.DAY,
};

/** Per-evidence-type qualifying config. Disabled entries don't contribute. */
export const evidenceConfigSchema = z.object({
  enabled: z.boolean(),
  window: durationSchema,
});
export type EvidenceConfig = z.infer<typeof evidenceConfigSchema>;

/**
 * One optional config per enum value: unknown keys drop and missing keys stay unset, so a
 * deploy that widens the enum can't fail old pods or tabs. The repo fills unset keys on read.
 */
function configPerEnumValue<E extends string, V extends z.ZodType>(
  values: readonly E[],
  valueSchema: V,
) {
  return z.object(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    Object.fromEntries(
      values.map((value) => [value, valueSchema.optional()]),
    ) as { [K in E]: z.ZodOptional<V> },
  );
}

/** Attribution config for one outcome. Every outcome shares this shape. */
export const outcomeAttributionConfigSchema = z
  .object({
    /** How reports split credit unless they ask for another distribution. */
    defaultDistribution: z.enum(AttributionDistribution),
    evidence: configPerEnumValue(
      Object.values(AttributionEvidenceType),
      evidenceConfigSchema,
    ),
  })
  .transform((config) => {
    const codeRedeemed = config.evidence[AttributionEvidenceType.CODE_REDEEMED];
    if (!codeRedeemed) {
      return config;
    }
    return {
      ...config,
      evidence: {
        ...config.evidence,
        [AttributionEvidenceType.CODE_REDEEMED]: {
          ...codeRedeemed,
          window: CODE_REDEEMED_WINDOW,
        },
      },
    };
  });
export type OutcomeAttributionConfig = z.infer<
  typeof outcomeAttributionConfigSchema
>;

/** Per-team attribution settings: the same config shape for every outcome type. */
export const attributionSettingsSchema = z.record(
  z.enum(AttributionOutcomeType),
  outcomeAttributionConfigSchema,
);
export type AttributionSettings = z.infer<typeof attributionSettingsSchema>;
